import { useOrganization } from '@hooks/queries/useOrganization';
import { useDashboardAccess } from '@hooks/useDashboardAccess';
import { useEntitlements } from '@hooks/useEntitlements';
import { useImageryFlags } from '@hooks/useImageryFlags';
import { useWorkspaceId } from '@hooks/useWorkspaceId';
import { routePaths } from '@screens/routes';
import { Layout } from 'antd';
import type { ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Actions } from '../../redux/actions';
import type { RootState } from '../../redux/reducers';
import { SideBarFooter } from './SideBarFooter';
import { SideBarSectionItem } from './SideBarSectionItem';
import styles from './_sidebar.module.scss';
import { getSidebarOptions, workspaceSidebarOptions } from './options';

const { toggleSidebar } = Actions;

const SideBar: React.FC = () => {
  const dispatch = useDispatch();
  const { propertyId } = useParams();
  const entitlements = useEntitlements();
  const { organization } = useOrganization();
  const workspaceId = useWorkspaceId();
  const dashboardAccess = useDashboardAccess();

  const {
    FlagShowYieldAnalysis,
    FlagShowDataLayers,
    FlagShowIntelligenceDashboard,
  } = useImageryFlags(organization);

  const { sidebarCollapsed } = useSelector((state: RootState) => state.App);

  const showWorkspaceOptions = !propertyId && FlagShowIntelligenceDashboard;

  return (
    <aside className={styles.sideBarWrapper}>
      <Layout.Sider
        className={styles.sideBarContainer}
        collapsed={sidebarCollapsed}
        trigger={<SideBarFooter />}
        onCollapse={() => dispatch(toggleSidebar())}
        collapsedWidth={64}
        width={220}
        collapsible
      >
        {propertyId && (
          <div className={styles.sectionsWrapper}>
            {getSidebarOptions.reduce<ReactElement[]>((acc, option) => {
              const hasEntitlementSupport = Boolean(
                option.entitlements?.every((key) => entitlements.has(key)) ?? false
              );

              const isPrescriptions = Boolean(
                option.entitlements?.some((key) => entitlements.has(key))
              );

              const isYieldAnalysis = option.path === routePaths.yieldAnalysis;
              const isDataLayers = option.path === routePaths.dataLayers;
              const isDashboard = option.path === routePaths.dashboard;

              const showOption = isDashboard
                ? dashboardAccess.status === 'allowed'
                : (isYieldAnalysis && FlagShowYieldAnalysis) ||
                  (isDataLayers && FlagShowDataLayers) ||
                  isPrescriptions ||
                  hasEntitlementSupport;

              if (showOption) {
                acc.push(
                  <SideBarSectionItem
                    organization={organization}
                    item={option}
                    key={`sidebar_item_${option.label}`}
                  />
                );
              }

              return acc;
            }, [])}
          </div>
        )}
        {showWorkspaceOptions && (
          <div className={styles.sectionsWrapper}>
            {workspaceSidebarOptions.reduce<ReactElement[]>((acc, option) => {
              // the dashboard URL needs a workspace id; My Cropwise falls back to the hall root
              if (
                option.path === routePaths.workspaceDashboard &&
                (!workspaceId || dashboardAccess.status !== 'allowed')
              ) {
                return acc;
              }

              acc.push(
                <SideBarSectionItem
                  organization={organization}
                  item={option}
                  workspaceId={workspaceId}
                  key={`sidebar_workspace_item_${option.label}`}
                />
              );

              return acc;
            }, [])}
          </div>
        )}
      </Layout.Sider>
    </aside>
  );
};

export default SideBar;
